// src/components/AIAutomation.js
// Automatisation — /ai-automation. When the AI agent sends a reply on its
// own, and what waits for the team. Zones: header · « À vérifier » (a brand's
// disabled rule that hides the general rule) · period + tiles (what the
// rules decided: GET /api/ai-automation/logs/stats) · views (?tab=):
//   rules    — « Qui peut envoyer seul » (read-only layers: B2B clients
//              GET /api/logistics/clients-edn, agent wake-up and assigned
//              tickets GET /api/automation/ticket-workflow/wakeup) + the
//              rules per scope. No account switch (users.automation):
//              automation is enabled per client / carrier (Kevin 29/10). No
//              rule fallback (« sinon assigné à X »): the backend never
//              applies it; its stored values stay in the PUT unchanged.
//   journal  — every decision of the rules (filters, table, drawer, CSV);
//   insights — the stored LLM analysis of the held replies.
// Any switch of a rule goes through a confirmation that says what changes:
// a setting acts on the next replies only (drafts already waiting stay).
// ?tab=workflow still redirects to /connectors?view=workflow.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlarmClock, Building2, ChevronRight, CircleCheck, Copy, Download, Gauge, Inbox, ListChecks, Pencil, Plus,
  RefreshCw, Search, Send, ShieldCheck, Sparkles, Trash2, TriangleAlert, UserRound, Zap, ZapOff,
} from 'lucide-react';
import {
  Badge, Banner, Card, ConfirmDialog, DescriptionList, Drawer, EmptyState, Field, Kpi, Meter, Modal, OverflowMenu,
  PageHeader, Pagination, Segmented, Switch, Tabs, Toast,
} from '../design/ui';
import {
  LOGS_PAGE_SIZE, PERIODS, automationAttention, automationKpis, brandIdOf, coverageRows, emptyRuleForm,
  insightsPayload, insightsQuery, journalFiltersActive, logQuery, parseNotEnough, ruleChangeKind, ruleCreatePayload,
  ruleFormFrom, rulePayload, sendingOverview, unjudgedAfterRemoval,
} from './aiAutomationModel';
import { formatCount, formatPercent, formatShortDay, formatStamp } from './clientsShared';
import './AIAutomation.css';

const TABS = ['rules', 'journal', 'insights'];
const EMPTY_FILTERS = { decision: '', scope: '', source: '', topic: '', assistant: '', q: '' };
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const AIAutomation = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const activeTab = TABS.includes(tabParam) ? tabParam : 'rules';
  useEffect(() => {
    if (tabParam === 'workflow') navigate('/connectors?view=workflow', { replace: true });
  }, [tabParam, navigate]);
  const setActiveTab = useCallback((tab) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (tab === 'rules') next.delete('tab'); else next.set('tab', tab);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // ── Data ──
  const [rules, setRules] = useState(null); // null = loading
  const [brands, setBrands] = useState([]);
  const [clients, setClients] = useState(null);
  const [wakeup, setWakeup] = useState(null);
  const [stats, setStats] = useState(undefined); // undefined = loading, null = unavailable
  const [statsDays, setStatsDays] = useState(30);
  const [journalBrand, setJournalBrand] = useState('');
  const [logs, setLogs] = useState(null);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logFilters, setLogFilters] = useState(EMPTY_FILTERS);
  const [logSearch, setLogSearch] = useState('');
  const [facets, setFacets] = useState(null); // {sources, topics, assistants}
  const [selectedLog, setSelectedLog] = useState(null);

  // ── Insights (LLM analysis of the held replies) ──
  const [insight, setInsight] = useState(undefined); // undefined = loading, null = none yet
  const [insightScope, setInsightScope] = useState('');
  const [insightGenerating, setInsightGenerating] = useState(false);
  const [insightError, setInsightError] = useState(null);
  const [copiedInstruction, setCopiedInstruction] = useState(null);

  // ── Dialogs ──
  const [ruleModal, setRuleModal] = useState(null); // {editing, form, error, saving}
  // {kind: activate | deactivate | delete, rule, form?, mode: toggle | save | delete, busy}
  const [confirm, setConfirm] = useState(null);

  const [toast, setToast] = useState(null);
  const showToast = useCallback((tone, text) => setToast({ tone, text }), []);
  useEffect(() => {
    if (!toast || toast.tone === 'danger') return undefined;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  // ══════════ Fetchers ══════════

  const fetchRules = useCallback(async () => {
    try {
      const res = await axios.get('/api/ai-automation/rules', { headers: authHeaders() });
      setRules(res.data || []);
    } catch (err) {
      console.error('Error fetching rules:', err);
      setRules([]);
      showToast('danger', t('aiAutomation.fetchError', 'Impossible de charger les règles.'));
    }
  }, [showToast, t]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`/api/ai-automation/logs/stats?${logQuery({ days: statsDays, brand: journalBrand, filters: logFilters })}`, { headers: authHeaders() });
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setStats(null);
    }
  }, [statsDays, journalBrand, logFilters]);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await axios.get(`/api/ai-automation/logs?${logQuery({
        days: statsDays, brand: journalBrand, filters: logFilters, page: logsPage, limit: LOGS_PAGE_SIZE, withDecision: true,
      })}`, { headers: authHeaders() });
      setLogs(res.data.logs || []);
      setLogsTotal(res.data.total || 0);
    } catch (err) {
      console.error('Error fetching logs:', err);
      setLogs([]);
    }
  }, [logsPage, statsDays, journalBrand, logFilters]);

  useEffect(() => {
    const headers = authHeaders();
    axios.get('/api/brands/list', { headers })
      .then((res) => setBrands(res.data || []))
      .catch((err) => console.error('Error fetching brands:', err));
    // Read-only layers of « Qui peut envoyer seul ».
    axios.get('/api/logistics/clients-edn', { headers })
      .then((res) => setClients(Array.isArray(res.data) ? res.data : null))
      .catch((err) => console.error('Error fetching B2B clients:', err));
    axios.get('/api/automation/ticket-workflow/wakeup', { headers })
      .then((res) => setWakeup(res.data || null))
      .catch((err) => console.error('Error fetching wake-up settings:', err));
    fetchRules();
  }, [fetchRules]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  useEffect(() => {
    if (activeTab === 'journal') fetchLogs();
  }, [activeTab, fetchLogs]);

  // Dropdown facets (sources / topics / AI agents), fetched once.
  useEffect(() => {
    if ((activeTab === 'journal' || activeTab === 'insights') && facets === null) {
      axios.get('/api/ai-automation/logs/facets', { headers: authHeaders() })
        .then((res) => setFacets(res.data))
        .catch((err) => {
          console.error('Error fetching log facets:', err);
          setFacets({ sources: [], topics: [], assistants: [] });
        });
    }
  }, [activeTab, facets]);

  // Debounced free-text search.
  useEffect(() => {
    const q = logSearch.trim();
    if (q === logFilters.q) return undefined;
    const timer = setTimeout(() => {
      setLogFilters((f) => ({ ...f, q }));
      setLogsPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [logSearch, logFilters.q]);

  // Latest stored analysis for the current parameters.
  useEffect(() => {
    if (activeTab !== 'insights') return;
    setInsight(undefined);
    setInsightError(null);
    axios.get(`/api/ai-automation/insights?${insightsQuery({ days: statsDays, brand: journalBrand, scope: insightScope })}`, { headers: authHeaders() })
      .then((res) => setInsight(res.data.insight))
      .catch((err) => {
        console.error('Error fetching insights:', err);
        setInsight(null);
      });
  }, [activeTab, statsDays, journalBrand, insightScope]);

  const generateInsight = async () => {
    setInsightGenerating(true);
    setInsightError(null);
    try {
      const res = await axios.post('/api/ai-automation/insights/generate', insightsPayload({
        days: statsDays, brand: journalBrand, scope: insightScope, language: i18n.language,
      }), { headers: authHeaders() });
      setInsight(res.data.insight);
    } catch (err) {
      const notEnough = parseNotEnough(err.response?.data?.detail);
      setInsightError(notEnough
        ? t('aiAutomation.notEnoughRejections',
          'Pas assez de refus à analyser sur cette période ({{found}} trouvé(s), minimum {{min}}). Élargissez la période ou les filtres.',
          notEnough)
        : t('aiAutomation.insightsError', 'Échec de la génération de l’analyse. Réessayez.'));
    } finally {
      setInsightGenerating(false);
    }
  };

  const copyInstruction = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedInstruction(key);
      setTimeout(() => setCopiedInstruction(null), 2500);
    } catch (err) {
      console.error('Clipboard copy failed:', err);
    }
  };

  // ══════════ Labels ══════════

  const lang = i18n.language || 'fr';
  const getBrandName = (id) => {
    if (!id) return t('aiAutomation.allBrands', 'Toutes les marques');
    const brand = brands.find((b) => brandIdOf(b) === String(id));
    return brand?.name || id;
  };
  const SCOPE_LABELS = {
    all: t('aiAutomationUi.scope.all', 'Toutes les réponses'),
    client: t('aiAutomationUi.scope.client', 'Réponses aux clients'),
    carrier: t('aiAutomationUi.scope.carrier', 'Réponses aux transporteurs'),
  };
  const SCOPE_SHORT = {
    all: t('aiAutomation.scopeAll', 'Tous les tickets'),
    client: t('aiAutomation.scopeClient', 'Client'),
    carrier: t('aiAutomation.scopeCarrier', 'Transporteur'),
  };
  const SOURCE_LABELS = {
    gmail: 'Gmail',
    outlook: 'Outlook',
    whatsapp: 'WhatsApp',
    web_form: t('aiAutomation.webForm', 'Formulaire web'),
    chatbot: t('aiAutomationUi.source.chatbot', 'Chatbot'),
    external_api: t('aiAutomationUi.source.externalApi', 'API externe'),
    carrier: t('aiAutomationUi.source.carrier', 'Réponse transporteur'),
  };
  const sourceLabel = (s) => SOURCE_LABELS[s] || (s ? s.replace(/_/g, ' ') : '—');
  const SEVERITY = {
    high: { tone: 'danger', label: t('aiAutomation.severityHigh', 'Impact fort') },
    medium: { tone: 'warning', label: t('aiAutomation.severityMedium', 'Impact moyen') },
    low: { tone: undefined, label: t('aiAutomation.severityLow', 'Impact faible') },
  };
  const periodLabel = {
    7: t('aiAutomation.last7', '7 derniers jours'),
    30: t('aiAutomation.last30', '30 derniers jours'),
    90: t('aiAutomation.last90', '90 derniers jours'),
    365: t('aiAutomation.lastYear', '12 derniers mois'),
  }[statsDays];
  const pct = (ratio) => formatPercent(ratio);
  const seconds = (ms) => `${(ms / 1000).toLocaleString(lang, { maximumFractionDigits: 1 })} s`;

  const ruleSentence = (rule) => {
    const threshold = t('aiAutomationUi.rule.threshold', 'part seule si la confiance atteint {{pct}} %', {
      pct: Math.round((rule.confidence_threshold || 0) * 100),
    });
    // Below the threshold the reply stays a draft (ai_automation_service:
    // can_send = False) — whatever fallback_action says: it is never applied.
    return `${threshold} · ${t('aiAutomation.ruleFallbackDraft', 'sinon brouillon à valider')}`;
  };

  // ══════════ Derived ══════════

  const kpis = automationKpis(stats);
  const overview = useMemo(() => sendingOverview({ clients, wakeup }), [clients, wakeup]);
  const attention = useMemo(() => automationAttention(rules), [rules]);
  const rows = useMemo(() => coverageRows(rules, brands), [rules, brands]);
  const activeRules = (rules || []).filter((rule) => rule.is_active).length;
  const filtered = journalFiltersActive(journalBrand, logFilters);

  // ══════════ Rules: dialogs and writes ══════════

  const openRuleModal = (rule = null, presetBrandId = '') => {
    setRuleModal({
      editing: rule,
      form: rule ? ruleFormFrom(rule) : emptyRuleForm(presetBrandId),
      error: null,
      saving: false,
    });
  };

  const setRuleForm = (patch) => setRuleModal((m) => ({ ...m, form: { ...m.form, ...patch }, error: null }));

  const doSaveRule = async (modal) => {
    const { form, editing } = modal;
    setRuleModal((m) => (m ? { ...m, saving: true, error: null } : m));
    try {
      if (editing) {
        await axios.put(`/api/ai-automation/rules/${editing.id}`, rulePayload(form), { headers: authHeaders() });
        showToast('success', t('aiAutomation.ruleUpdated', 'Règle mise à jour.'));
      } else {
        await axios.post('/api/ai-automation/rules', ruleCreatePayload(form), { headers: authHeaders() });
        showToast('success', t('aiAutomation.ruleCreated', 'Règle créée.'));
      }
      setRuleModal(null);
      fetchRules();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setRuleModal((m) => (m ? {
        ...m, saving: false,
        error: typeof detail === 'string' ? detail : t('aiAutomation.saveError', 'Échec de l’enregistrement de la règle.'),
      } : m));
    }
  };

  const saveRule = () => {
    const { form, editing } = ruleModal;
    if (!form.instructions.trim()) {
      setRuleModal((m) => ({ ...m, error: t('aiAutomation.instructionsRequired', 'Les instructions sont requises.') }));
      return;
    }
    const kind = ruleChangeKind(editing, form.is_active);
    if (kind) {
      setConfirm({ kind, mode: 'save', rule: editing || { brand_id: form.brand_id || null, scope: form.scope }, form, busy: false });
      return;
    }
    doSaveRule(ruleModal);
  };

  const confirmChange = async () => {
    const { mode, rule, kind } = confirm;
    if (mode === 'save') {
      const modal = ruleModal;
      setConfirm(null);
      await doSaveRule(modal);
      return;
    }
    setConfirm((c) => ({ ...c, busy: true }));
    try {
      if (mode === 'delete') {
        await axios.delete(`/api/ai-automation/rules/${rule.id}`, { headers: authHeaders() });
        showToast('success', t('aiAutomation.ruleDeleted', 'Règle supprimée.'));
      } else {
        await axios.put(`/api/ai-automation/rules/${rule.id}`, { is_active: kind === 'activate' }, { headers: authHeaders() });
        showToast('success', kind === 'activate'
          ? t('aiAutomationUi.toast.activated', 'Règle activée : elle juge les prochaines réponses.')
          : t('aiAutomationUi.toast.deactivated', 'Règle désactivée.'));
      }
      setConfirm(null);
      fetchRules();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setConfirm(null);
      showToast('danger', typeof detail === 'string' ? detail : (mode === 'delete'
        ? t('aiAutomation.deleteError', 'Échec de la suppression de la règle.')
        : t('aiAutomation.toggleError', 'Échec de l’activation/désactivation.')));
    }
  };

  // ══════════ CSV export ══════════

  const handleExportCSV = async () => {
    try {
      const res = await axios.get(`/api/ai-automation/logs/export?${logQuery({
        days: statsDays, brand: journalBrand, filters: logFilters, withDecision: true,
      })}`, { headers: authHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'automation_logs.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting logs:', err);
      showToast('danger', t('aiAutomation.exportError', 'Échec de l’export CSV.'));
    }
  };

  const setFilter = (patch) => { setLogFilters((f) => ({ ...f, ...patch })); setLogsPage(1); };
  const openJournal = (decision) => { setFilter({ decision }); setActiveTab('journal'); };

  // ══════════ Zones ══════════

  const subtitle = () => {
    if (rules === null) return t('aiAutomationUi.subtitleLoading', 'Quand l’agent IA envoie seul, et ce qui attend votre équipe.');
    const parts = [rules.length
      ? t('aiAutomationUi.subtitle.rules', '{{count}} règles actives sur {{total}}', { count: activeRules, total: rules.length })
      : t('aiAutomationUi.subtitle.noRules', 'Aucune règle d’envoi automatique')];
    if (overview.clients && overview.clients.total > 0) {
      parts.push(t('aiAutomationUi.subtitle.clients', '{{count}} clients B2B en envoi automatique sur {{total}}', {
        count: overview.clients.automated, total: overview.clients.total,
      }));
    }
    return parts.join(' · ');
  };

  const renderAttention = () => {
    if (!attention.length) return null;
    return (
      <Card
        icon={TriangleAlert}
        iconTone="warning"
        title={t('aiAutomationUi.attention.title', 'À vérifier')}
        badge={<Badge tone="warning">{attention.length}</Badge>}
        flush
        className="aa-v2__attention"
      >
        <ul className="u-list">
          {attention.map((item) => (
            <li className="u-row" key={item.key}>
              <Badge tone="warning" dot>{t('aiAutomationUi.attention.watch', 'À surveiller')}</Badge>
              <div className="u-row__main">
                <div className="u-row__title">
                  {t('aiAutomationUi.attention.shadow', 'La règle désactivée de {{brand}} écarte la règle « Toutes les marques »', {
                    brand: getBrandName(item.rule.brand_id),
                  })}
                </div>
                <div className="u-row__meta u-row__meta--wrap">
                  {t('settingsPolishUi.automation.shadowDetail', '{{scope}} de cette marque ne sont pas jugées : le réglage du client B2B décide seul. Réactivez-la, ou supprimez-la pour appliquer la règle générale.', {
                    scope: SCOPE_LABELS[item.rule.scope] || item.rule.scope,
                  })}
                </div>
              </div>
              <div className="u-row__side">
                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => openRuleModal(item.rule)}>
                  {t('aiAutomationUi.open', 'Ouvrir')}
                  <ChevronRight aria-hidden="true" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      </Card>
    );
  };

  const renderPeriodBar = () => (
    <div className="aa-v2__period">
      <Segmented
        label={t('aiAutomation.period', 'Période')}
        value={statsDays}
        onChange={(days) => { setStatsDays(days); setLogsPage(1); }}
        options={PERIODS.map((days) => ({
          value: days,
          label: days === 365 ? t('aiAutomationUi.period.year', '12 mois') : t('aiAutomationUi.period.days', '{{days}} jours', { days }),
        }))}
      />
      <span className="aa-v2__scope">
        {t('aiAutomationUi.period.scope', 'Réponses jugées par vos règles · {{period}}', { period: periodLabel })}
        {filtered && ` · ${t('aiAutomationUi.period.filtered', 'filtres du journal appliqués')}`}
      </span>
    </div>
  );

  const renderKpis = () => {
    if (stats === undefined) {
      return (
        <div className="u-kpi-row aa-v2__kpis" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton aa-v2__kpi-skeleton" />)}
        </div>
      );
    }
    if (!kpis) return null;
    const journal = activeTab === 'journal';
    return (
      <div className={`u-kpi-row aa-v2__kpis${kpis.avgConfidence === null ? ' aa-v2__kpis--3' : ''}`}>
        <Kpi
          icon={ListChecks}
          label={t('aiAutomationUi.kpi.judged', 'Réponses jugées')}
          value={formatCount(kpis.total)}
          context={t('aiAutomationUi.kpi.judgedContext', 'par une règle active · {{period}}', { period: periodLabel })}
          active={journal && !logFilters.decision}
          onClick={() => openJournal('')}
        />
        <Kpi
          icon={Send}
          tone="success"
          label={t('aiAutomationUi.kpi.approved', 'Envoi autorisé')}
          value={formatCount(kpis.approved)}
          context={kpis.approvalRate === null
            ? t('aiAutomationUi.kpi.none', 'aucune réponse jugée')
            : t('aiAutomationUi.kpi.approvedContext', '{{rate}} des réponses jugées', { rate: pct(kpis.approvalRate) })}
          meter={kpis.approvalRate === null ? undefined : {
            value: kpis.approved, max: kpis.total, tone: 'success', label: t('aiAutomationUi.kpi.approved', 'Envoi autorisé'),
          }}
          active={journal && logFilters.decision === 'approved'}
          onClick={() => openJournal('approved')}
        />
        <Kpi
          icon={Inbox}
          tone="warning"
          label={t('aiAutomationUi.kpi.held', 'Gardées en brouillon')}
          value={formatCount(kpis.rejected)}
          context={t('aiAutomationUi.kpi.heldContext', 'retenues pour votre équipe')}
          active={journal && logFilters.decision === 'rejected'}
          onClick={() => openJournal('rejected')}
        />
        {kpis.avgConfidence !== null && (
          <Kpi
            icon={Gauge}
            label={t('aiAutomationUi.kpi.confidence', 'Confiance moyenne')}
            value={pct(kpis.avgConfidence)}
            context={kpis.avgSeconds !== null
              ? t('aiAutomationUi.kpi.confidenceContext', 'jugement en {{time}} en moyenne', { time: seconds(kpis.avgSeconds * 1000) })
              : undefined}
          />
        )}
      </div>
    );
  };

  // ---- Rules view -----------------------------------------------------------

  const wakeupText = (mode) => ({
    off: [t('aiAutomationUi.who.wakeupOff', 'Relances automatiques'), t('aiAutomationUi.who.wakeupOffMeta', 'Des minuteries relancent ; l’agent ne revient pas seul sur les dossiers.'), undefined],
    pilot: [t('aiAutomationUi.who.wakeupPilot', 'Pilote'), t('aiAutomationUi.who.wakeupPilotMeta', 'L’agent revient sur les dossiers en attente et prépare ses relances en brouillon.'), 'warning'],
    live: [t('aiAutomationUi.who.wakeupLive', 'Agent IA'), overview.wakeup?.liveSince
      ? t('aiAutomationUi.who.wakeupLiveSince', 'L’agent revient seul sur les dossiers en attente depuis le {{date}} et juge s’il faut relancer.', { date: formatShortDay(overview.wakeup.liveSince) })
      : t('aiAutomationUi.who.wakeupLiveMeta', 'L’agent revient seul sur les dossiers en attente et juge s’il faut relancer.'), 'success'],
  }[mode] || [mode, '', undefined]);

  const assignedText = (mode) => ({
    mute: [t('aiAutomationUi.who.assignedMute', 'L’IA se tait'), t('aiAutomationUi.who.assignedMuteMeta', 'L’IA ne traite pas le ticket ; la personne assignée est prévenue.')],
    drafts: [t('aiAutomationUi.who.assignedDrafts', 'Brouillons pour l’équipe'), t('aiAutomationUi.who.assignedDraftsMeta', 'Rien ne part au client ni au transporteur sans la personne assignée.')],
    assist: [t('aiAutomationUi.who.assignedAssist', 'Brouillons + actions transporteur'), t('aiAutomationUi.who.assignedAssistMeta', 'La réponse au client reste un brouillon ; les demandes au transporteur suivent l’automatisation du client.')],
  }[mode] || [mode, '']);

  const renderWhoSends = () => {
    const { clients: c, wakeup: w, assigned } = overview;
    if (!c && !w) return null;
    return (
      <Card
        icon={ShieldCheck}
        title={t('aiAutomationUi.who.title', 'Qui peut envoyer seul')}
        description={t('aiAutomationUi.who.desc', 'Les réglages qui décident si une réponse de l’IA part sans relecture, du plus prioritaire au plus général.')}
        footerNote={t('aiAutomationUi.who.footer', 'Le chatbot du site répond toujours en direct. Un changement vaut pour les prochaines réponses : les brouillons déjà en attente restent à valider.')}
        flush
      >
        <ul className="u-list aa-v2__who">
          {assigned && (
            <li className="u-row">
              <span className="aa-v2__who-icon" aria-hidden="true"><UserRound strokeWidth={1.8} /></span>
              <div className="u-row__main">
                <div className="u-row__title">
                  {t('aiAutomationUi.who.assigned', 'Tickets assignés à l’équipe')}
                  <Badge>{assignedText(assigned)[0]}</Badge>
                </div>
                <div className="u-row__meta u-row__meta--wrap">{assignedText(assigned)[1]}</div>
              </div>
              <div className="u-row__side">
                <Link className="u-btn u-btn--ghost u-btn--sm" to="/followup-settings">
                  {t('aiAutomationUi.who.followups', 'Relances')}
                  <ChevronRight aria-hidden="true" />
                </Link>
              </div>
            </li>
          )}
          {w && (
            <li className="u-row">
              <span className="aa-v2__who-icon" aria-hidden="true"><AlarmClock strokeWidth={1.8} /></span>
              <div className="u-row__main">
                <div className="u-row__title">
                  {t('aiAutomationUi.who.wakeup', 'Réveil de l’agent')}
                  <Badge tone={wakeupText(w.mode)[2]} dot={w.mode !== 'off'}>{wakeupText(w.mode)[0]}</Badge>
                </div>
                <div className="u-row__meta u-row__meta--wrap">{wakeupText(w.mode)[1]}</div>
              </div>
              <div className="u-row__side">
                <Link className="u-btn u-btn--ghost u-btn--sm" to="/followup-settings">
                  {t('aiAutomationUi.who.followups', 'Relances')}
                  <ChevronRight aria-hidden="true" />
                </Link>
              </div>
            </li>
          )}
          {c && (
            <li className="u-row">
              <span className="aa-v2__who-icon" aria-hidden="true"><Building2 strokeWidth={1.8} /></span>
              <div className="u-row__main">
                <div className="u-row__title">
                  {t('aiAutomationUi.who.clients', 'Clients B2B')}
                  {c.total > 0 && (
                    <Badge tone={c.automated ? 'success' : undefined} dot={c.automated > 0}>
                      {t('aiAutomationUi.who.clientsBadge', '{{count}} en automatique sur {{total}}', { count: c.automated, total: c.total })}
                    </Badge>
                  )}
                </div>
                <div className="u-row__meta u-row__meta--wrap">
                  {c.total > 0
                    ? t('aiAutomationUi.who.clientsMeta', 'Le réglage du client décide pour ses tickets ouverts après l’activation ; une règle active juge en plus chaque réponse.')
                    : t('settingsPolishUi.automation.clientsNone', 'Aucun client B2B configuré : l’envoi automatique s’active client par client.')}
                </div>
              </div>
              <div className="u-row__side">
                <Link className="u-btn u-btn--ghost u-btn--sm" to={c.automated ? '/clients-instructions?filter=auto' : '/clients-instructions'}>
                  {t('aiAutomationUi.who.clientsLink', 'Clients B2B')}
                  <ChevronRight aria-hidden="true" />
                </Link>
              </div>
            </li>
          )}
        </ul>
      </Card>
    );
  };

  const renderRuleLine = (rule) => (
    <div key={rule.id} className={`aa-v2__rule${rule.is_active ? '' : ' is-off'}`}>
      <Badge tone={rule.is_active ? 'accent' : undefined}>{SCOPE_LABELS[rule.scope] || rule.scope}</Badge>
      <span className="aa-v2__rule-text" title={rule.instructions}>
        {rule.is_active
          ? ruleSentence(rule)
          : t('settingsPolishUi.automation.ruleInactive', 'Désactivée : le réglage du client B2B décide')}
      </span>
      <span className="aa-v2__rule-side">
        <Switch
          id={`aa-rule-${rule.id}`}
          checked={rule.is_active}
          onChange={(next) => setConfirm({ kind: next ? 'activate' : 'deactivate', mode: 'toggle', rule, busy: false })}
          label={<span className="aa-v2__sr">{rule.is_active ? t('aiAutomation.deactivate', 'Désactiver') : t('aiAutomation.activate', 'Activer')}</span>}
        />
        <OverflowMenu
          label={t('aiAutomationUi.ruleActions', 'Actions de la règle')}
          items={[
            { label: t('aiAutomation.edit', 'Modifier'), icon: Pencil, onClick: () => openRuleModal(rule) },
            { separator: true },
            { label: t('aiAutomation.delete', 'Supprimer'), icon: Trash2, danger: true, onClick: () => setConfirm({ kind: 'delete', mode: 'delete', rule, busy: false }) },
          ]}
        />
      </span>
    </div>
  );

  const renderRules = () => (
    <Card
      icon={Zap}
      title={t('aiAutomationUi.rules.title', 'Règles d’envoi automatique')}
      badge={rules && rules.length ? <Badge tone={activeRules ? 'accent' : undefined}>{t('aiAutomationUi.rules.active', '{{count}} actives', { count: activeRules })}</Badge> : null}
      description={t('aiAutomationUi.rules.desc', 'Avec une règle active, l’IA juge chaque réponse avant l’envoi : elle part seule si sa confiance atteint le seuil, sinon elle reste en brouillon. La règle d’une marque remplace celle de « Toutes les marques ».')}
      flush
    >
      {rules === null ? (
        <div className="aa-v2__skeleton" aria-busy="true">
          {[0, 1, 2].map((i) => <span key={i} className="u-skeleton" />)}
        </div>
      ) : (
        <ul className="u-list aa-v2__cov">
          {rows.map((row) => (
            <li key={row.brandId || 'global'} className="u-row aa-v2__cov-row">
              <div className="aa-v2__cov-scope">
                <span className="aa-v2__cov-name">{row.isGlobal ? t('aiAutomation.allBrands', 'Toutes les marques') : row.brand.name}</span>
                {row.isGlobal && <span className="aa-v2__cov-hint">{t('aiAutomation.covFallback', 'portée par défaut')}</span>}
              </div>
              <div className="aa-v2__cov-body">
                {row.state === 'own' ? row.rules.map(renderRuleLine) : (
                  <div className="aa-v2__rule">
                    <span className="aa-v2__rule-text aa-v2__rule-text--muted">
                      {row.state === 'covered'
                        ? t('aiAutomation.covCoveredByGlobal', 'Couvert par « Toutes les marques »')
                        : t('settingsPolishUi.automation.ruleNone', 'Aucune règle : le réglage du client B2B décide')}
                    </span>
                    <span className="aa-v2__rule-side">
                      <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => openRuleModal(null, row.brandId)}>
                        <Plus aria-hidden="true" />
                        {t('aiAutomationUi.rule.add', 'Ajouter une règle')}
                      </button>
                    </span>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );

  // ---- Journal view ---------------------------------------------------------

  const renderDecisionBadge = (decision) => (decision === 'approved'
    ? <Badge tone="success" dot>{t('aiAutomationUi.decision.approved', 'Autorisée')}</Badge>
    : <Badge tone="warning" dot>{t('aiAutomationUi.decision.rejected', 'Retenue')}</Badge>);

  const renderJournal = () => {
    const decisionCount = (key) => {
      if (!kpis) return undefined;
      return formatCount(key === 'approved' ? kpis.approved : key === 'rejected' ? kpis.rejected : kpis.total);
    };
    return (
      <section className="u-card aa-v2__journal">
        <div className="u-toolbar">
          <div className="u-input-group u-input-group--lead">
            <Search className="u-input-group__icon" aria-hidden="true" />
            <input
              type="search"
              className="u-input"
              value={logSearch}
              onChange={(e) => setLogSearch(e.target.value)}
              placeholder={t('aiAutomation.searchPlaceholder', 'Objet, n° de suivi, raison…')}
              aria-label={t('aiAutomation.search', 'Recherche')}
            />
          </div>
          <Segmented
            label={t('aiAutomation.decision', 'Décision')}
            value={logFilters.decision}
            onChange={(decision) => setFilter({ decision })}
            options={[
              { value: '', label: t('aiAutomation.all', 'Toutes'), count: decisionCount('') },
              { value: 'approved', label: t('aiAutomationUi.decision.approvedPlural', 'Autorisées'), count: decisionCount('approved') },
              { value: 'rejected', label: t('aiAutomationUi.decision.rejectedPlural', 'Retenues'), count: decisionCount('rejected') },
            ]}
          />
          <span className="u-toolbar__spacer" />
          <button
            type="button"
            className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
            onClick={() => { fetchStats(); fetchLogs(); }}
            title={t('aiAutomation.refresh', 'Actualiser')}
            aria-label={t('aiAutomation.refresh', 'Actualiser')}
          >
            <RefreshCw aria-hidden="true" />
          </button>
          <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={handleExportCSV}>
            <Download aria-hidden="true" />
            {t('aiAutomation.exportCSV', 'Export CSV')}
          </button>
        </div>
        <div className="u-toolbar aa-v2__filters">
          {brands.length > 0 && (
            <select className="u-select" value={journalBrand} onChange={(e) => { setJournalBrand(e.target.value); setLogsPage(1); }} aria-label={t('aiAutomation.brand', 'Marque')}>
              <option value="">{t('aiAutomation.allBrands', 'Toutes les marques')}</option>
              {brands.map((b) => <option key={brandIdOf(b)} value={brandIdOf(b)}>{b.name}</option>)}
            </select>
          )}
          <select className="u-select" value={logFilters.scope} onChange={(e) => setFilter({ scope: e.target.value })} aria-label={t('aiAutomation.scope', 'Portée')}>
            <option value="">{t('aiAutomationUi.filters.allScopes', 'Toutes les réponses')}</option>
            <option value="client">{SCOPE_LABELS.client}</option>
            <option value="carrier">{SCOPE_LABELS.carrier}</option>
          </select>
          <select className="u-select" value={logFilters.source} onChange={(e) => setFilter({ source: e.target.value })} aria-label={t('aiAutomation.source', 'Source')}>
            <option value="">{t('aiAutomationUi.filters.allSources', 'Toutes les sources')}</option>
            {(facets?.sources || []).map((s) => <option key={s} value={s}>{sourceLabel(s)}</option>)}
            {facets !== null && logFilters.source && !(facets.sources || []).includes(logFilters.source) && (
              <option value={logFilters.source}>{sourceLabel(logFilters.source)}</option>
            )}
          </select>
          {(facets?.topics || []).length > 0 && (
            <select className="u-select" value={logFilters.topic} onChange={(e) => setFilter({ topic: e.target.value })} aria-label={t('aiAutomation.ticketTopic', 'Sujet')}>
              <option value="">{t('aiAutomationUi.filters.allTopics', 'Tous les sujets')}</option>
              {facets.topics.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
            </select>
          )}
          {(facets?.assistants || []).length > 0 && (
            <select className="u-select" value={logFilters.assistant} onChange={(e) => setFilter({ assistant: e.target.value })} aria-label={t('aiAutomation.aiAgent', 'Agent IA')}>
              <option value="">{t('aiAutomationUi.filters.allAgents', 'Tous les agents IA')}</option>
              {facets.assistants.map((a) => <option key={a.assistant_id} value={a.assistant_id}>{a.name}</option>)}
            </select>
          )}
          {filtered && (
            <button
              type="button"
              className="u-btn u-btn--ghost u-btn--sm"
              onClick={() => { setJournalBrand(''); setLogSearch(''); setLogFilters((f) => ({ ...EMPTY_FILTERS, decision: f.decision })); setLogsPage(1); }}
            >
              {t('aiAutomationUi.filters.clear', 'Effacer les filtres')}
            </button>
          )}
          <span className="u-toolbar__spacer" />
          {logs !== null && (
            <span className="aa-v2__count">
              {t('aiAutomationUi.journal.count', '{{total}} décisions', { count: logsTotal, total: formatCount(logsTotal) })}
            </span>
          )}
        </div>
        {logs === null ? (
          <div className="aa-v2__skeleton" aria-busy="true">
            {Array.from({ length: 6 }, (_, i) => <span key={i} className="u-skeleton" />)}
          </div>
        ) : logs.length === 0 ? (
          <EmptyState
            icon={ListChecks}
            title={filtered || logFilters.decision
              ? t('aiAutomation.noLogs', 'Aucune décision d’automatisation ne correspond aux filtres.')
              : t('aiAutomationUi.journal.emptyTitle', 'Aucune réponse jugée sur la période')}
          >
            {filtered || logFilters.decision
              ? t('aiAutomationUi.journal.emptyFiltered', 'Changez la période ou les filtres.')
              : t('aiAutomationUi.journal.emptyHint', 'Une décision apparaît ici chaque fois qu’une règle active juge une réponse de l’IA.')}
          </EmptyState>
        ) : (
          <>
            <div className="u-table-scroll">
              <table className="u-table aa-v2__table">
                <thead>
                  <tr>
                    <th style={{ width: '128px' }}>{t('aiAutomation.date', 'Date')}</th>
                    <th style={{ width: '31%' }}>{t('aiAutomation.ticketCol', 'Ticket')}</th>
                    <th style={{ width: '14%' }}>{t('aiAutomation.aiAgent', 'Agent IA')}</th>
                    <th style={{ width: '13%' }}>{t('aiAutomation.source', 'Source')}</th>
                    <th style={{ width: '13%' }}>{t('aiAutomation.decision', 'Décision')}</th>
                    <th style={{ width: '96px' }} className="aa-v2__num">{t('aiAutomation.confidence', 'Confiance')}</th>
                    <th style={{ width: '80px' }} className="aa-v2__num">{t('aiAutomation.processing', 'Temps')}</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr
                      key={log.id}
                      className={`aa-v2__row${selectedLog?.id === log.id ? ' is-selected' : ''}`}
                      tabIndex={0}
                      onClick={() => setSelectedLog(log)}
                      onKeyDown={(e) => { if (e.key === 'Enter') setSelectedLog(log); }}
                    >
                      <td className="u-num">{formatStamp(log.created_at) || '—'}</td>
                      <td title={log.ticket_subject || undefined}>
                        <span className="aa-v2__ellipsis">{log.ticket_subject || '—'}</span>
                        <span className="u-cell-sub">
                          {[log.ticket_topic, SCOPE_SHORT[log.scope] || log.scope].filter(Boolean).join(' · ')}
                        </span>
                      </td>
                      <td title={log.assistant_name || undefined}>{log.assistant_name || <span className="u-muted">—</span>}</td>
                      <td title={sourceLabel(log.source)}>{sourceLabel(log.source)}</td>
                      <td>{renderDecisionBadge(log.decision)}</td>
                      <td className="aa-v2__num">{typeof log.confidence === 'number' ? pct(log.confidence) : '—'}</td>
                      <td className="aa-v2__num">{typeof log.processing_time_ms === 'number' ? seconds(log.processing_time_ms) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={logsPage}
              pageSize={LOGS_PAGE_SIZE}
              total={logsTotal}
              onChange={setLogsPage}
              summary={({ first, last, total }) => t('aiAutomationUi.pageSummary', '{{first}}–{{last}} sur {{total}}', { first, last, total: formatCount(total) })}
              previousLabel={t('common.previousPage', 'Page précédente')}
              nextLabel={t('common.nextPage', 'Page suivante')}
            />
          </>
        )}
      </section>
    );
  };

  // ---- Insights view --------------------------------------------------------

  const renderInsights = () => (
    <div className="aa-v2__stack">
      <Card
        icon={Sparkles}
        title={t('aiAutomationUi.insights.title', 'Analyse des réponses retenues')}
        description={insight
          ? t('aiAutomation.insightsMeta', 'Générée le {{date}} · {{analyzed}} refus analysés sur {{rejected}} · {{evals}} évaluations sur la période', {
            date: formatStamp(insight.generated_at),
            analyzed: insight.logs_analyzed,
            rejected: insight.rejected_total,
            evals: insight.total_evaluations,
          })
          : t('aiAutomationUi.insights.desc', 'L’IA relit les réponses retenues de la période et dit ce qui bloque l’envoi automatique.')}
        actions={(
          <>
            {brands.length > 0 && (
              <select className="u-select aa-v2__select" value={journalBrand} onChange={(e) => setJournalBrand(e.target.value)} aria-label={t('aiAutomation.brand', 'Marque')}>
                <option value="">{t('aiAutomation.allBrands', 'Toutes les marques')}</option>
                {brands.map((b) => <option key={brandIdOf(b)} value={brandIdOf(b)}>{b.name}</option>)}
              </select>
            )}
            <select className="u-select aa-v2__select" value={insightScope} onChange={(e) => setInsightScope(e.target.value)} aria-label={t('aiAutomation.scope', 'Portée')}>
              <option value="">{t('aiAutomationUi.filters.allScopes', 'Toutes les réponses')}</option>
              <option value="client">{SCOPE_LABELS.client}</option>
              <option value="carrier">{SCOPE_LABELS.carrier}</option>
            </select>
            <button type="button" className="u-btn u-btn--primary" onClick={generateInsight} disabled={insightGenerating || insight === undefined}>
              <Sparkles aria-hidden="true" />
              {insightGenerating
                ? t('aiAutomation.insightsGenerating', 'Analyse en cours…')
                : insight
                  ? t('aiAutomation.insightsRegenerate', 'Régénérer l’analyse')
                  : t('aiAutomation.insightsGenerate', 'Analyser les refus')}
            </button>
          </>
        )}
      >
        {insightError && <Banner tone="danger">{insightError}</Banner>}
        {insight === undefined ? (
          <div className="aa-v2__skeleton aa-v2__skeleton--inline" aria-busy="true">
            {[0, 1, 2].map((i) => <span key={i} className="u-skeleton" />)}
          </div>
        ) : !insight ? (
          <EmptyState icon={Sparkles} title={t('aiAutomation.insightsEmptyTitle', 'Aucune analyse pour ces critères')}>
            {t('aiAutomationUi.insights.emptyBody', 'Lancez l’analyse : elle regroupe les réponses retenues par vos règles, dit pourquoi elles l’ont été et ce qu’il faudrait préciser dans vos règles ou les consignes de vos agents.')}
          </EmptyState>
        ) : (
          <p className="aa-v2__summary">{insight.summary}</p>
        )}
      </Card>

      {insight && insight.patterns.length > 0 && (
        <Card
          icon={ListChecks}
          title={t('aiAutomationUi.insights.patterns', 'Ce qui retient les réponses')}
          badge={<Badge>{insight.patterns.length}</Badge>}
          flush
        >
          <ul className="u-list">
            {insight.patterns.map((p, idx) => (
              <li key={idx} className="u-row aa-v2__pattern">
                <div className="u-row__main">
                  <div className="u-row__title">
                    {p.title}
                    <Badge tone={SEVERITY[p.severity]?.tone}>{SEVERITY[p.severity]?.label || p.severity}</Badge>
                  </div>
                  <p className="aa-v2__pattern-desc">{p.description}</p>
                  <DescriptionList
                    items={[
                      { label: t('aiAutomation.rootCause', 'Pourquoi'), value: p.root_cause },
                      { label: t('aiAutomation.recommendationLabel', 'Recommandation'), value: p.recommendation },
                    ]}
                  />
                  {p.suggested_instruction && (
                    <div className="aa-v2__snippet">
                      <div className="aa-v2__snippet-head">
                        <span className="u-overline">{t('aiAutomation.suggestedInstruction', 'Instruction prête à coller dans vos règles')}</span>
                        <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => copyInstruction(p.suggested_instruction, idx)}>
                          {copiedInstruction === idx ? <CircleCheck aria-hidden="true" /> : <Copy aria-hidden="true" />}
                          {copiedInstruction === idx ? t('aiAutomation.copied', 'Copié !') : t('aiAutomation.copy', 'Copier')}
                        </button>
                      </div>
                      <pre>{p.suggested_instruction}</pre>
                    </div>
                  )}
                </div>
                {p.estimated_share_pct > 0 && (
                  <div className="u-row__side aa-v2__share">
                    <span className="aa-v2__share-value u-num">
                      {t('aiAutomation.patternShare', '≈ {{pct}} % des refus', { pct: Math.round(p.estimated_share_pct) })}
                    </span>
                    <Meter value={p.estimated_share_pct} max={100} tone={p.severity === 'high' ? 'danger' : p.severity === 'medium' ? 'warning' : undefined} label={p.title} />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {insight && (insight.rule_gaps.length > 0 || insight.working_well.length > 0) && (
        <div className="u-grid-2">
          {insight.rule_gaps.length > 0 && (
            <Card icon={TriangleAlert} iconTone="warning" title={t('aiAutomation.ruleGapsTitle', 'Non couvert par vos règles')}>
              <ul className="aa-v2__bullets">
                {insight.rule_gaps.map((g, idx) => <li key={idx}>{g}</li>)}
              </ul>
            </Card>
          )}
          {insight.working_well.length > 0 && (
            <Card icon={CircleCheck} iconTone="success" title={t('aiAutomation.workingWellTitle', 'Ce qui fonctionne bien')}>
              <ul className="aa-v2__bullets">
                {insight.working_well.map((w, idx) => <li key={idx}>{w}</li>)}
              </ul>
            </Card>
          )}
        </div>
      )}
    </div>
  );

  // ---- Dialogs --------------------------------------------------------------

  const renderRuleModal = () => {
    const { form, editing, error, saving } = ruleModal;
    const thresholdPct = Math.round(form.confidence_threshold * 100);
    return (
      <Modal
        wide
        icon={Zap}
        title={editing ? t('aiAutomation.editRule', 'Modifier la règle') : t('aiAutomation.createRule', 'Créer une règle')}
        description={t('aiAutomationUi.modal.desc', 'L’IA juge chaque réponse de cette portée avant l’envoi. Le réglage vaut pour les prochaines réponses.')}
        onClose={() => setRuleModal(null)}
        closeLabel={t('common.close', 'Fermer')}
        footer={(
          <>
            <button type="button" className="u-btn u-btn--secondary" onClick={() => setRuleModal(null)} disabled={saving}>
              {t('common.cancel', 'Annuler')}
            </button>
            <button type="button" className="u-btn u-btn--primary" onClick={saveRule} disabled={saving}>
              {saving ? t('common.saving', 'Enregistrement…') : t('common.save', 'Enregistrer')}
            </button>
          </>
        )}
      >
        <div className="u-form">
          {error && <Banner tone="danger">{error}</Banner>}
          <div className="u-form-grid">
            <Field label={t('aiAutomation.brand', 'Marque')} htmlFor="aa-brand" help={editing ? t('aiAutomationUi.modal.brandLocked', 'La marque ne change pas après la création.') : undefined}>
              {editing ? (
                <input id="aa-brand" className="u-input" value={getBrandName(editing.brand_id)} disabled readOnly />
              ) : (
                <select id="aa-brand" className="u-select" value={form.brand_id} onChange={(e) => setRuleForm({ brand_id: e.target.value })}>
                  <option value="">{t('aiAutomation.allBrands', 'Toutes les marques')}</option>
                  {brands.map((b) => <option key={brandIdOf(b)} value={brandIdOf(b)}>{b.name}</option>)}
                </select>
              )}
            </Field>
            <Field label={t('aiAutomation.scope', 'Portée')} htmlFor="aa-scope">
              <select id="aa-scope" className="u-select" value={form.scope} onChange={(e) => setRuleForm({ scope: e.target.value })}>
                <option value="all">{SCOPE_LABELS.all}</option>
                <option value="client">{SCOPE_LABELS.client}</option>
                <option value="carrier">{SCOPE_LABELS.carrier}</option>
              </select>
            </Field>
          </div>
          <Field
            label={t('aiAutomation.sectionInstructions', 'Instructions')}
            htmlFor="aa-instructions"
            help={t('aiAutomation.instructionsHint', 'Critères que l’IA applique pour juger si sa propre réponse peut partir sans validation humaine.')}
          >
            <textarea
              id="aa-instructions"
              className="u-textarea"
              rows={6}
              maxLength={100000}
              value={form.instructions}
              onChange={(e) => setRuleForm({ instructions: e.target.value })}
              placeholder={t('aiAutomation.instructionsPlaceholder', 'Ex. : approuver uniquement les réponses de suivi de colis sans engagement financier…')}
            />
          </Field>
          <Field
            label={(
              <span className="aa-v2__threshold-label">
                {t('aiAutomation.threshold', 'Seuil de confiance')}
                <span className="u-num">{thresholdPct} %</span>
              </span>
            )}
            htmlFor="aa-threshold"
            help={t('settingsPolishUi.automation.thresholdHint', 'En dessous du seuil, la réponse n’est pas envoyée : elle reste en brouillon à valider.')}
          >
            <input
              id="aa-threshold"
              className="aa-v2__range"
              type="range"
              min="50"
              max="100"
              step="5"
              value={thresholdPct}
              onChange={(e) => setRuleForm({ confidence_threshold: Number(e.target.value) / 100 })}
            />
          </Field>
          <div className="aa-v2__activation">
            <Switch
              id="aa-active"
              checked={form.is_active}
              onChange={(next) => setRuleForm({ is_active: next })}
              label={form.is_active
                ? t('aiAutomationUi.modal.active', 'Active — elle juge les prochaines réponses dès l’enregistrement')
                : t('settingsPolishUi.automation.modalInactive', 'Désactivée — le réglage du client B2B décide')}
            />
          </div>
        </div>
      </Modal>
    );
  };

  const renderConfirm = () => {
    const { kind, rule, form, busy } = confirm;
    const brand = getBrandName(rule?.brand_id);
    const scope = SCOPE_LABELS[(form || rule)?.scope] || (form || rule)?.scope;
    const after = unjudgedAfterRemoval(overview);
    const extras = [];
    if (kind !== 'activate') {
      if (after.automatedClients) {
        extras.push(t('aiAutomationUi.confirm.unjudgedClients', '{{count}} clients B2B en envoi automatique enverront leurs réponses sans ce contrôle.', { count: after.automatedClients }));
      }
    }
    extras.push(t('aiAutomationUi.confirm.future', 'Cela vaut pour les prochaines réponses : les brouillons déjà en attente restent à valider.'));
    const texts = {
      activate: {
        icon: Zap,
        title: t('aiAutomationUi.confirm.activateTitle', 'Activer l’envoi automatique pour {{brand}} ?', { brand }),
        message: t('aiAutomationUi.confirm.activate', '{{scope}} : une réponse de l’IA partira sans relecture quand sa confiance atteint {{pct}} %, sauf pour les clients B2B en envoi manuel. Sinon, elle reste en brouillon.', {
          scope, pct: Math.round(((form || rule)?.confidence_threshold || 0) * 100),
        }),
        label: t('aiAutomationUi.confirm.activateLabel', 'Activer l’envoi automatique'),
      },
      deactivate: {
        icon: ZapOff,
        title: t('aiAutomationUi.confirm.deactivateTitle', 'Désactiver la règle de {{brand}} ?', { brand }),
        message: t('settingsPolishUi.automation.confirmDeactivate', '{{scope}} ne seront plus jugées par cette règle : le réglage du client B2B décide.', { scope }),
        label: t('aiAutomation.deactivate', 'Désactiver'),
      },
      delete: {
        icon: Trash2,
        title: t('aiAutomationUi.confirm.deleteTitle', 'Supprimer la règle de {{brand}} ?', { brand }),
        message: rule?.brand_id
          ? t('settingsPolishUi.automation.confirmDeleteBrand', '{{scope}} de cette marque suivront la règle « Toutes les marques » si elle les couvre, sinon le réglage du client B2B. La règle et ses instructions sont effacées.', { scope })
          : t('settingsPolishUi.automation.confirmDeleteGlobal', '{{scope}} des marques sans règle propre ne seront plus jugées : le réglage du client B2B décidera seul. La règle et ses instructions sont effacées.', { scope }),
        label: t('aiAutomation.delete', 'Supprimer'),
      },
    }[kind];
    return (
      <ConfirmDialog
        icon={texts.icon}
        title={texts.title}
        message={`${texts.message} ${extras.join(' ')}`}
        confirmLabel={busy ? t('common.saving', 'Enregistrement…') : texts.label}
        cancelLabel={t('common.cancel', 'Annuler')}
        onConfirm={confirmChange}
        onCancel={() => setConfirm(null)}
        busy={busy}
      />
    );
  };

  const renderDrawer = () => {
    const log = selectedLog;
    return (
      <Drawer
        title={t('aiAutomation.logDetail', 'Décision d’automatisation')}
        description={formatStamp(log.created_at)}
        icon={ListChecks}
        onClose={() => setSelectedLog(null)}
        closeLabel={t('common.close', 'Fermer')}
        footer={log.ticket_id ? (
          <button type="button" className="u-btn u-btn--primary" onClick={() => navigate(`/tickets/${log.ticket_id}/messages`)}>
            {t('aiAutomation.goToTicket', 'Ouvrir le ticket')}
            <ChevronRight aria-hidden="true" />
          </button>
        ) : null}
      >
        <div className="aa-v2__drawer">
          <div className="aa-v2__drawer-decision">
            {renderDecisionBadge(log.decision)}
            <span className="aa-v2__drawer-text">
              {log.decision === 'approved'
                ? t('aiAutomationUi.decision.approvedLong', 'La règle a autorisé l’envoi sans relecture')
                : t('aiAutomationUi.decision.rejectedLong', 'La règle a gardé la réponse en brouillon pour l’équipe')}
              {typeof log.confidence === 'number' && ` · ${t('aiAutomation.confidenceValue', 'confiance {{pct}} %', { pct: (log.confidence * 100).toFixed(0) })}`}
            </span>
          </div>
          <DescriptionList
            items={[
              { label: t('aiAutomation.ticketSubject', 'Objet du ticket'), value: log.ticket_subject },
              { label: t('aiAutomation.ticketTopic', 'Sujet'), value: log.ticket_topic },
              { label: t('aiAutomation.aiAgent', 'Agent IA'), value: log.assistant_name },
              { label: t('aiAutomation.trackingNumber', 'N° de suivi'), value: log.tracking_number ? <span className="u-code">{log.tracking_number}</span> : null },
              { label: t('aiAutomation.ticketStatus', 'Statut du ticket'), value: log.ticket_status ? t(`ticketStatuses.${log.ticket_status}`, log.ticket_status) : null },
              { label: t('aiAutomation.source', 'Source'), value: sourceLabel(log.source) },
              { label: t('aiAutomation.scope', 'Portée'), value: SCOPE_LABELS[log.scope] || log.scope },
              { label: t('aiAutomation.brand', 'Marque'), value: log.brand_id ? getBrandName(log.brand_id) : null },
              { label: t('aiAutomation.processing', 'Temps'), value: typeof log.processing_time_ms === 'number' ? seconds(log.processing_time_ms) : null },
            ]}
          />
          {log.reason && (
            <section className="aa-v2__quote-block">
              <h3 className="u-overline">{t('aiAutomation.reason', 'Raison')}</h3>
              <p className="aa-v2__quote">{log.reason}</p>
            </section>
          )}
          {log.customer_message_snippet && (
            <section className="aa-v2__quote-block">
              <h3 className="u-overline">{t('aiAutomation.customerMsg', 'Message client')}</h3>
              <p className="aa-v2__quote">{log.customer_message_snippet}</p>
            </section>
          )}
          {log.ai_response_snippet && (
            <section className="aa-v2__quote-block">
              <h3 className="u-overline">{t('aiAutomation.aiResponse', 'Réponse de l’IA')}</h3>
              <p className="aa-v2__quote">{log.ai_response_snippet}</p>
            </section>
          )}
          <details className="u-disclosure aa-v2__ids">
            <summary>
              <ChevronRight aria-hidden="true" />
              {t('aiAutomation.technicalIds', 'Identifiants techniques')}
            </summary>
            <DescriptionList
              items={[
                { label: t('aiAutomation.ticketId', 'Ticket'), value: <span className="u-code">{log.ticket_id || '—'}</span> },
                { label: t('aiAutomation.aiAgent', 'Agent IA'), value: log.assistant_id ? <span className="u-code">{log.assistant_id}</span> : null },
                { label: t('aiAutomation.messageId', 'Message'), value: log.message_id ? <span className="u-code">{log.message_id}</span> : null },
                { label: t('aiAutomation.logId', 'Log'), value: <span className="u-code">{log.id}</span> },
              ]}
            />
          </details>
        </div>
      </Drawer>
    );
  };

  // ══════════ Render ══════════

  const showKpis = stats === undefined || activeTab !== 'rules' || (kpis && kpis.total > 0);

  return (
    <div className="u-page aa-v2">
      <PageHeader
        title={t('aiAutomation.title', 'Automatisation')}
        subtitle={subtitle()}
        actions={(
          <button type="button" className="u-btn u-btn--primary" onClick={() => openRuleModal()}>
            <Plus aria-hidden="true" />
            {t('aiAutomation.createRule', 'Créer une règle')}
          </button>
        )}
      />

      <div className="u-well">
        {renderAttention()}
        {showKpis && renderPeriodBar()}
        {showKpis && renderKpis()}
        <Tabs
          label={t('aiAutomation.title', 'Automatisation')}
          active={activeTab}
          onChange={setActiveTab}
          tabs={[
            { key: 'rules', label: t('aiAutomation.rulesTab', 'Règles'), icon: Zap, count: rules ? rules.length : undefined },
            { key: 'journal', label: t('aiAutomation.journalTab', 'Journal'), icon: ListChecks },
            { key: 'insights', label: t('aiAutomation.insightsTab', 'Recommandations'), icon: Sparkles },
          ]}
        />
        <div role="tabpanel" className="aa-v2__stack">
          {activeTab === 'rules' && (
            <>
              {renderRules()}
              {renderWhoSends()}
            </>
          )}
          {activeTab === 'journal' && renderJournal()}
          {activeTab === 'insights' && renderInsights()}
        </div>
      </div>

      {selectedLog && renderDrawer()}
      {ruleModal && renderRuleModal()}
      {confirm && renderConfirm()}

      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>
          {toast.text}
        </Toast>
      )}
    </div>
  );
};

export default AIAutomation;
